import React, { useState } from 'react';
import {
  useGetRetentionScheduleQuery,
  useUpdateRetentionRuleMutation,
} from '../../store/api/localApi';
import { ResolvedRetentionRule, RETENTION_RECORD_TYPE_LABELS } from '../../types/retention';

/**
 * Retention schedule editor (Task 21). Each record type shows its resolved
 * period (editable) AND its provenance string READ-ONLY beside it — the honesty
 * requirement: every default period renders its source in-product. Child
 * protection's period is HOLD and its provenance says it is UK/Barnardos-derived
 * guidance, not Irish statute.
 *
 * CLOCKS ONLY — nothing here deletes. Editing a period only changes how long a
 * record would be kept and what the overview counts.
 */

const HOLD_LABEL = 'Hold (kept indefinitely)';

const RetentionRuleRow: React.FC<{ locationId: string; rule: ResolvedRetentionRule }> = ({
  locationId,
  rule,
}) => {
  const [updateRule, { isLoading }] = useUpdateRetentionRuleMutation();
  // Local draft of the period. `''` renders as HOLD (null).
  const [draft, setDraft] = useState<string>(
    rule.periodMonths === null ? '' : String(rule.periodMonths)
  );
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const onSave = async () => {
    setError(null);
    setSaved(false);
    let periodMonths: number | null;
    if (draft.trim() === '') {
      periodMonths = null; // HOLD
    } else {
      const n = Number(draft);
      if (!Number.isInteger(n) || n < 0) {
        setError('Enter a whole number of months, or leave blank to hold indefinitely.');
        return;
      }
      periodMonths = n;
    }
    try {
      await updateRule({ locationId, recordType: rule.recordType, periodMonths }).unwrap();
      setSaved(true);
    } catch {
      setError('Could not save. Please try again.');
    }
  };

  return (
    <tr data-testid={`retention-row-${rule.recordType}`} className="border-t border-gray-200">
      <td className="py-3 pr-4 align-top">
        <div className="font-medium text-gray-900">
          {RETENTION_RECORD_TYPE_LABELS[rule.recordType]}
        </div>
        <div className="text-xs text-gray-500">
          {rule.trigger === 'CHILD_LEFT'
            ? 'Clock starts when the child leaves.'
            : 'Clock starts when the record is created.'}
        </div>
      </td>
      <td className="py-3 pr-4 align-top">
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={0}
            step={1}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setSaved(false);
            }}
            placeholder="Hold"
            aria-label={`Retention period in months for ${
              RETENTION_RECORD_TYPE_LABELS[rule.recordType]
            }`}
            data-testid={`retention-period-${rule.recordType}`}
            className="w-24 rounded border border-gray-300 px-2 py-1 text-sm"
          />
          <span className="text-xs text-gray-500">
            {draft.trim() === '' ? HOLD_LABEL : 'months'}
          </span>
          <button
            type="button"
            onClick={onSave}
            disabled={isLoading}
            data-testid={`retention-save-${rule.recordType}`}
            className="rounded bg-primary-600 px-3 py-1 text-sm font-medium text-white disabled:opacity-50"
          >
            {isLoading ? 'Saving…' : 'Save'}
          </button>
          {saved && (
            <span
              data-testid={`retention-saved-${rule.recordType}`}
              className="text-xs text-green-600"
            >
              Saved
            </span>
          )}
        </div>
        {error && (
          <div
            data-testid={`retention-error-${rule.recordType}`}
            className="mt-1 text-xs text-red-600"
          >
            {error}
          </div>
        )}
        <div className="mt-1 text-xs text-gray-400">
          Source:{' '}
          {rule.source === 'DEFAULT'
            ? 'seeded default'
            : rule.source === 'ORG'
            ? 'organisation override'
            : 'this location'}
        </div>
      </td>
      <td className="py-3 align-top">
        {/* Provenance: rendered VERBATIM, read-only, beside the editable period. */}
        <p
          data-testid={`retention-provenance-${rule.recordType}`}
          className="max-w-md text-xs text-gray-600"
        >
          {rule.provenance}
        </p>
      </td>
    </tr>
  );
};

const RetentionScheduleEditor: React.FC<{ locationId: string }> = ({ locationId }) => {
  const { data, isLoading, isError } = useGetRetentionScheduleQuery({ locationId });

  if (isLoading) {
    return <p className="text-sm text-gray-500">Loading retention schedule…</p>;
  }
  if (isError || !data) {
    return (
      <p data-testid="retention-schedule-error" className="text-sm text-red-600">
        Could not load the retention schedule.
      </p>
    );
  }

  return (
    <section data-testid="retention-schedule">
      <h3 className="text-lg font-semibold text-gray-900">Retention schedule</h3>
      <p className="mt-1 text-sm text-gray-500">
        How long each kind of record is kept, and where that period comes from. Editing a period
        changes only how long records are retained — nothing is ever deleted from this screen.
      </p>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-gray-400">
              <th className="py-2 pr-4 font-medium">Record type</th>
              <th className="py-2 pr-4 font-medium">Keep for</th>
              <th className="py-2 font-medium">Where this comes from (provenance)</th>
            </tr>
          </thead>
          <tbody>
            {data.schedule.map((rule) => (
              <RetentionRuleRow key={rule.recordType} locationId={locationId} rule={rule} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default RetentionScheduleEditor;
